import React, { useCallback, useMemo, useRef } from 'react';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { InputAdornment, FormControl, FilledInput, InputLabel } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import { DragHandle } from '@mui/icons-material';
import indexToLetter from '@dyl/utils/indexToLetter';
import Draggable from 'react-draggable';
import useSize from '@dyl/hooks/useSize';
import PropTypes from 'prop-types';

const useAnswerStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'row', width: '200px', marginBottom: '2px' },
  field: { flex: 1 },
}));

export default function Answer({ index, answerIndex, pageIndex, isDistractor, ELEMENT_COUNT }) {
  const classes = useAnswerStyles();
  const [items, setItems] = useTaskField('items');
  const [answer, setAnswer] = useTaskField('items', index, 'answers', answerIndex, 'text');
  const [displayOrder] = useTaskField('items', index, 'answers', answerIndex, 'display_order');
  const localDisplayOrder = ((displayOrder - 1) % ELEMENT_COUNT) + 1;
  const onAnswerChange = useChangeEvent(setAnswer);

  const move = useCallback((offset) => {
    if (offset === 0) {
      return;
    }

    const targetItem = items.find(x => x.index === index);
    if (!targetItem) {
      return;
    }

    const flatAnswers = targetItem.answers.map((answer, ai) => ({
      index: targetItem.index,
      answerIndex: ai,
      display_order: answer.display_order,
    }));

    const srcIndex = flatAnswers.findIndex(
      x => x.index === index && x.answerIndex === answerIndex
    );
    if (srcIndex === -1) {
      return;
    }

    const newIndex = (srcIndex + offset + flatAnswers.length) % flatAnswers.length;
    if (newIndex < 0 || newIndex >= flatAnswers.length) {
      return;
    }

    const updatedList = [...flatAnswers];
    const [moved] = updatedList.splice(srcIndex, 1);
    updatedList.splice(newIndex, 0, moved);

    const reordered = updatedList.map((a, idx) => ({
      ...a,
      display_order: flatAnswers[idx].display_order,
    }));

    const findUpdatedAnswer = (reordered, index, ai) =>
      reordered.find(x => x.index === index && x.answerIndex === ai);

    const updateAnswers = (answers, reordered, index) => {
      return answers
        .map((answer, ai) => {
          const updatedAnswer = findUpdatedAnswer(reordered, index, ai);
          return {
            ...answer,
            display_order: updatedAnswer
              ? updatedAnswer.display_order
              : answer.display_order,
          };
        })
        .sort((a, b) => a.display_order - b.display_order);
    };

    const updateItems = (items, index, reordered) => {
      return items.map(item =>
        item.index !== index
          ? item
          : {
            ...item,
            answers: updateAnswers(item.answers, reordered, index),
          }
      );
    };
    setItems(prev => updateItems(prev, index, reordered));

  }, [items, index, answerIndex, setItems]);

  const dragHandleClass = `answerDragHandle-${index}`;
  const containerRef = useRef();
  const { height: containerHeight } = useSize(containerRef);

  const bounds = useMemo(() => ({
    top: - (localDisplayOrder - 1) * containerHeight,
    bottom: (ELEMENT_COUNT - localDisplayOrder) * containerHeight,
    left: 0,
    right: 0,
  }), [containerHeight, localDisplayOrder, ELEMENT_COUNT]);

  const label = isDistractor
    ? `Distractor`
    : `Answer ${indexToLetter(index)}`;

  return (
    <Draggable
      handle={`.${dragHandleClass}`}
      position={{ x: 0, y: 0 }}
      axis="y"
      bounds={bounds}
      onStop={(e, data) => {
        const rect = containerRef.current.getBoundingClientRect();
        const delta = data.y;
        const indexOffset = Math.round(delta / rect.height);

        if (indexOffset !== 0) {
          move(indexOffset);
        }
      }}
    >
      <div className={classes.root} ref={containerRef}>
        <FormControl
          variant="filled"
          className={classes.field}
          sx={{ width: '200px' }}
        >
          <InputLabel
            htmlFor={`filled-adornment-${index}`}
          >
            {label}
          </InputLabel>

          <FilledInput
            id={`filled-adornment-${index}`}
            value={answer}
            onChange={onAnswerChange}
            inputProps={{ maxLength: 10 }}
            endAdornment={
              <InputAdornment position="end" style={{ cursor: 'grab' }}>
                <DragHandle className={dragHandleClass} />
              </InputAdornment>
            }
          />
        </FormControl>
      </div>
    </Draggable>
  );
}
Answer.propTypes = {
  index: PropTypes.number.isRequired,
  answerIndex: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  isDistractor: PropTypes.bool.isRequired,
  ELEMENT_COUNT: PropTypes.number.isRequired,
};
